import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import CustomModal from '../customModal';
import VirtualizedList from '../virtualizedList';
import User from './user';

const useStyles = makeStyles(({ palette: { primary } }) => ({
  usersWrapper: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 80,
    maxHeight: 160,
  },
  userWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  usersViewport: {
    maxHeight: 160,
  },
  description: {
    fontSize: 14,
    lineHeight: '18px',
  },
  confirmButton: {
    backgroundColor: primary.red1,
    '&:hover': {
      backgroundColor: primary.red1,
    },
    '&:disabled': {
      backgroundColor: primary.bluish5,
    },
  },
  scrollY: {
    right: -19,
  },
}));

const GroupUserActionDialog = ({
  translations,
  isOpened,
  users,
  onClose,
  onConfirm,
}) => {
  const classes = useStyles();
  const [selectedUsers, setSelectedUsers] = useState([]);

  const getVirtualUserSize = useCallback(
    index => (index === users.length - 1 ? 24 : 32),
    [users.length]
  );

  const checkIsUserSelected = useCallback(
    user => selectedUsers.includes(user.id),
    [selectedUsers]
  );

  const handleClose = () => {
    setSelectedUsers([]);
    onClose();
  };

  const handleSave = async () => {
    await onConfirm(selectedUsers);
    handleClose();
  };

  const onChange = useCallback(
    userId => {
      const isChecked = selectedUsers.includes(userId);

      setSelectedUsers([
        ...(isChecked
          ? selectedUsers.filter(uId => uId !== userId)
          : [...selectedUsers, userId]),
      ]);
    },
    [selectedUsers]
  );

  const renderUser = useCallback(
    user => {
      const isSelected = checkIsUserSelected(user);
      return <User user={user} isSelected={isSelected} onChange={onChange} />;
    },
    [checkIsUserSelected, onChange]
  );

  return (
    <CustomModal
      descriptionClass={classes.description}
      confirmButtonClass={classes.confirmButton}
      confirmButtonLabel={translations.save}
      closeButtonLabel={translations.cancel}
      title={translations.title}
      description={translations.description}
      isConfirmDisabled={false}
      isOpened={isOpened}
      onClose={handleClose}
      onConfirm={handleSave}
      isMedium
    >
      <div className={classes.usersWrapper}>
        <VirtualizedList
          viewportClass={classes.usersViewport}
          verticalScrollClass={classes.scrollY}
          items={users}
          count={users?.length ?? 0}
          gap={8}
          estimateSize={getVirtualUserSize}
          renderItem={renderUser}
        />
      </div>
    </CustomModal>
  );
};

GroupUserActionDialog.propTypes = {
  isOpened: PropTypes.bool.isRequired,
  translations: PropTypes.object.isRequired,
  users: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default GroupUserActionDialog;
